import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Rocket, Play, Pause, Loader2, Sparkles, CheckCircle2, Clock,
  Megaphone, Code2, Search, Share2, Target, Mail, Brain, X, Activity,
  MessageCircle, ExternalLink, ShieldAlert, Send, Headphones,
  History, LayoutGrid, ChevronDown, ChevronRight, CircleAlert,
} from 'lucide-react';
import DashboardShell, { ghostChip } from '../components/DashboardShell';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { toast } from 'sonner';

const DASHBOARD_URL = '/dashboard';
const outputsPath = (sessionId) => (
  sessionId ? `/ai-cofounder/outputs?session_id=${encodeURIComponent(sessionId)}` : '/ai-cofounder/outputs'
);

const GUIDE_PATH = '/platform-guide?section=ai-cofounder';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ROLE_META = {
  plan: { label: 'Strategy', icon: Brain, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
  research: { label: 'Research', icon: Search, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
  build: { label: 'Build', icon: Code2, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
  marketing: { label: 'Marketing', icon: Mail, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
  ads: { label: 'Ads', icon: Megaphone, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
  seo: { label: 'SEO', icon: Search, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
  social: { label: 'Social', icon: Share2, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
  leads: { label: 'Leads', icon: Target, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
  support: { label: 'Support', icon: Headphones, color: 'text-[#fbbf0f]', ring: 'ring-[#fbbf0f]/35', bg: 'bg-[#fbbf0f]/10' },
};

const btnPrimary = 'inline-flex items-center justify-center gap-2 rounded-full bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d] shadow-[0_4px_0_0_#16130d] px-5 py-2.5 text-sm font-extrabold hover:bg-[#ffd84a] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_#16130d] transition disabled:opacity-55';
const btnDark = 'inline-flex items-center justify-center gap-2 rounded-full bg-[#16130d] text-white border-2 border-[#16130d] shadow-[0_4px_0_0_rgba(0,0,0,0.5)] px-5 py-2.5 text-sm font-extrabold hover:bg-black transition disabled:opacity-55';
const card = 'rounded-2xl border border-white/12 bg-[#1c1914] shadow-[0_6px_0_0_rgba(0,0,0,0.35)]';
const cardYellow = 'rounded-2xl border-2 border-[#16130d] bg-[#fbbf0f] text-[#16130d] shadow-[0_6px_0_0_#16130d]';
const inputCls = 'w-full rounded-xl bg-[#0e0c0a] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-[#8a8072] outline-none focus:border-[#fbbf0f] focus:ring-2 focus:ring-[#fbbf0f]/25';

const roleMeta = (r) => ROLE_META[r] || ROLE_META.plan;

/** Compact success checklist — platform Ready vs Connect gaps vs optional. */
const SuccessChecklist = ({ readiness, navigate, compact = false }) => {
  const [showOptional, setShowOptional] = useState(false);
  if (!readiness?.items?.length) return null;
  const platform = readiness.groups?.platform || readiness.items.filter((i) => i.group === 'platform');
  const connect = readiness.groups?.connect || readiness.items.filter((i) => i.group === 'connect');
  const optional = readiness.groups?.optional || readiness.items.filter((i) => i.group === 'optional');
  const blurb = readiness.summary?.blurb
    || 'Autonomous still drafts until you Connect email/ads/social for live send.';
  const missingCount = readiness.summary?.missing_connect_count
    ?? connect.filter((i) => i.status === 'missing').length;

  const Row = ({ item }) => {
    const ready = item.status === 'ready';
    const optionalUnset = item.status === 'optional';
    const platformMissing = item.group === 'platform' && item.status === 'missing';
    // Prefer API cta_label when not ready; null from API means Ready (do not ||-fallback).
    const cta = ready
      ? null
      : (item.cta_label
        || ((optionalUnset || platformMissing) ? 'Set up'
          : item.group === 'connect' ? 'Connect' : 'Set up'));
    const isSetUp = cta === 'Set up';
    return (
      <button
        type="button"
        onClick={() => item.href && navigate(item.href)}
        className={`w-full flex items-start gap-2.5 rounded-xl px-2.5 py-2 text-left transition ${
          ready
            ? 'hover:bg-white/5'
            : isSetUp
              ? 'hover:bg-white/5'
              : 'bg-[#fbbf0f]/12 hover:bg-[#fbbf0f]/20 border border-[#fbbf0f]/35'
        }`}
        data-testid={`cofounder-ready-${item.id}`}
      >
        {ready ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-emerald-400" />
        ) : isSetUp ? (
          <Clock className="h-4 w-4 shrink-0 mt-0.5 text-[#8a8072]" />
        ) : (
          <CircleAlert className="h-4 w-4 shrink-0 mt-0.5 text-[#fbbf0f]" />
        )}
        <span className="min-w-0 flex-1">
          <span className={`block text-xs font-extrabold ${ready ? 'text-[#e8e0d4]' : isSetUp ? 'text-[#c4b8a5]' : 'text-white'}`}>
            {item.label}
            {ready ? (
              <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-400">Ready</span>
            ) : cta ? (
              <span className={`ml-1.5 text-[10px] font-bold uppercase tracking-wide ${isSetUp ? 'text-[#a89882]' : 'text-[#fbbf0f]'}`}>
                {cta}
              </span>
            ) : null}
          </span>
          {!compact && item.hint && (
            <span className="block text-[11px] text-[#a89882] mt-0.5 leading-snug font-medium">{item.hint}</span>
          )}
        </span>
        {!ready && item.href && (
          <ExternalLink className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${isSetUp ? 'text-[#8a8072]' : 'text-[#fbbf0f]'}`} />
        )}
      </button>
    );
  };

  return (
    <div
      className={`${card} p-4 ${compact ? 'border-[#fbbf0f]/30' : ''}`}
      data-testid="cofounder-success-checklist"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <h2
            className="text-xs font-sans font-semibold uppercase tracking-wider text-[#fbbf0f]"
            style={{ fontFamily: "'IBM Plex Sans', Manrope, sans-serif" }}
          >
            To succeed
          </h2>
          <p className="text-[11px] text-[#c4b8a5] mt-1 font-medium leading-snug">{blurb}</p>
        </div>
        {missingCount > 0 && (
          <span className="shrink-0 rounded-full bg-[#fbbf0f] text-[#16130d] border border-[#16130d] px-2 py-0.5 text-[10px] font-extrabold">
            {missingCount} to connect
          </span>
        )}
      </div>

      <div className="space-y-0.5 mt-2">
        <div className="text-[10px] font-extrabold uppercase tracking-wide text-[#8a8072] px-2.5 pt-1">Platform</div>
        {platform.map((item) => <Row key={item.id} item={item} />)}
        <div className="text-[10px] font-extrabold uppercase tracking-wide text-[#8a8072] px-2.5 pt-2">Go live</div>
        {connect.map((item) => <Row key={item.id} item={item} />)}
      </div>

      {optional.length > 0 && (
        <div className="mt-2 border-t border-white/10 pt-2">
          <button
            type="button"
            onClick={() => setShowOptional((v) => !v)}
            className="flex items-center gap-1.5 text-[11px] font-extrabold text-[#a89882] hover:text-[#fbbf0f] px-2.5 py-1"
            data-testid="cofounder-ready-optional-toggle"
          >
            {showOptional ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Optional power-ups
          </button>
          {showOptional && (
            <div className="space-y-0.5 mt-0.5">
              {optional.map((item) => <Row key={item.id} item={item} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const stripMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text || '';
  let out = text;
  const trimmed = out.trim();
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      const obj = JSON.parse(trimmed);
      const lines = [];
      const walk = (o, prefix = '') => {
        if (Array.isArray(o)) o.forEach((v) => walk(v, prefix));
        else if (o && typeof o === 'object') {
          Object.entries(o).forEach(([k, v]) => {
            const label = k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            if (v && typeof v === 'object') { lines.push(`${prefix}${label}:`); walk(v, `${prefix}  `); }
            else lines.push(`${prefix}${label}: ${v}`);
          });
        } else lines.push(`${prefix}${o}`);
      };
      walk(obj);
      out = lines.join('\n');
    } catch { /* ignore */ }
  }
  return out
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1$2')
    .replace(/(^|[^_])_(?!\s)([^_\n]+?)_/g, '$1$2')
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')
    .replace(/\[(.+?)\]\((.+?)\)/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/^\s*>\s?/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

function authHeaders() {
  const token = (localStorage.getItem('token') || '').trim();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const timeAgo = (iso) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.floor(s)}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
};

function StatusPill({ status, killSwitch }) {
  if (killSwitch) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-extrabold border-2 shadow-[0_2px_0_0_#16130d] bg-[#16130d] text-white border-[#16130d]" data-testid="cofounder-status-pill">
        <span className="h-2 w-2 rounded-full bg-red-400 animate-pulse" />Kill switch ON
      </span>
    );
  }
  const map = {
    running: { t: 'Running', c: 'bg-[#fbbf0f] text-[#16130d] border-[#16130d]', dot: 'bg-[#16130d] animate-pulse' },
    paused: { t: 'Paused', c: 'bg-[#fdf3ce] text-[#16130d] border-[#16130d]', dot: 'bg-[#ef9f00]' },
    idle: { t: 'Standing by', c: 'bg-white text-[#16130d] border-[#16130d]', dot: 'bg-[#fbbf0f]' },
    stopped: { t: 'Stopped', c: 'bg-[#1c1914] text-[#e4d9c6] border-white/25', dot: 'bg-[#8a8072]' },
  };
  const m = map[status] || map.stopped;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-extrabold border-2 shadow-[0_2px_0_0_#16130d] ${m.c}`} data-testid="cofounder-status-pill">
      <span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.t}
    </span>
  );
}

/** UI modes map to backend levels: Guided→L1, Autonomous→L2. L0/L3 stay API-compatible but hidden. */
const AUTONOMY_MODES = [
  {
    value: 'L1',
    option: 'Guided',
    summary: 'Auto drafts; you approve send, publish, and spend.',
  },
  {
    value: 'L2',
    option: 'Autonomous',
    summary: 'Runs Launch Brand; you still approve gated live actions.',
  },
];

/** Normalize stored L0–L3 into the two UI options without rewriting backend state. */
const autonomyUiLevel = (level) => (
  (level === 'L2' || level === 'L3') ? 'L2' : 'L1'
);

const autonomySummary = (level) => (
  AUTONOMY_MODES.find((row) => row.value === autonomyUiLevel(level))?.summary
  || AUTONOMY_MODES[0].summary
);

const autonomyLabel = (level) => (
  AUTONOMY_MODES.find((row) => row.value === autonomyUiLevel(level))?.option
  || 'Guided'
);

/** Autonomy mode + kill switch — shared on home picker and workspace. Domain dials hidden (API still accepts). */
const AutonomyControls = ({
  autonomyLevel = 'L1',
  killSwitch = false,
  busy,
  onPatchAutonomy,
  onToggleKillSwitch,
  variant = 'dark', // dark | light (on yellow card)
  align = 'center', // center | start
}) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const uiLevel = autonomyUiLevel(autonomyLevel);
  const labelCls = variant === 'light'
    ? 'text-[11px] font-extrabold text-[#16130d]'
    : 'text-[11px] font-extrabold text-[#fbbf0f]';
  const selectCls = variant === 'light'
    ? 'rounded-full border-2 border-[#16130d] bg-white px-3 py-1.5 text-[11px] font-extrabold text-[#16130d]'
    : 'rounded-full border-2 border-[#fbbf0f]/60 bg-[#0e0c0a] px-3 py-1.5 text-[11px] font-extrabold text-white';
  const killOffCls = variant === 'light'
    ? `${ghostChip} border-[#16130d]`
    : `${ghostChip} border-white/25 text-[#e4d9c6]`;
  const helpBtnCls = variant === 'light'
    ? 'text-[10px] font-extrabold text-[#16130d]/70 underline underline-offset-2 hover:text-[#16130d]'
    : 'text-[10px] font-extrabold text-[#fbbf0f]/80 underline underline-offset-2 hover:text-[#fbbf0f]';
  const mutedCls = variant === 'light'
    ? 'text-[11px] font-medium text-[#3a3327]'
    : 'text-[11px] font-medium text-[#c4b8a5]';
  const alignText = align === 'start' ? 'text-left' : 'text-center';
  return (
    <div className={`flex flex-col gap-1.5 ${align === 'start' ? 'items-start' : 'items-center'}`} data-testid="cofounder-autonomy-controls">
      <div className={`flex flex-wrap items-center gap-2 ${align === 'start' ? 'justify-start' : 'justify-center'}`}>
        <label className={labelCls} htmlFor="cofounder-autonomy-dial">
          Autonomy
        </label>
        <select
          id="cofounder-autonomy-dial"
          disabled={busy}
          value={uiLevel}
          onChange={(e) => onPatchAutonomy(e.target.value)}
          className={selectCls}
          data-testid="cofounder-autonomy-dial"
          title={autonomySummary(uiLevel)}
        >
          {AUTONOMY_MODES.map((row) => (
            <option key={row.value} value={row.value}>{row.option}</option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy}
          onClick={() => onToggleKillSwitch(!killSwitch)}
          className={`${killSwitch ? 'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-extrabold border-2 border-red-500 bg-red-600 text-white' : killOffCls}`}
          data-testid="cofounder-kill-switch"
          title="Stop all autonomous actions immediately"
        >
          <ShieldAlert className="h-3.5 w-3.5" />
          {killSwitch ? 'Kill switch ON' : 'Kill switch'}
        </button>
      </div>
      <p className={`max-w-xl ${mutedCls} ${alignText}`} data-testid="cofounder-autonomy-summary">
        {autonomySummary(uiLevel)}
      </p>
      <button
        type="button"
        onClick={() => setHelpOpen((v) => !v)}
        className={helpBtnCls}
        data-testid="cofounder-autonomy-help-toggle"
        aria-expanded={helpOpen}
      >
        {helpOpen ? 'Hide level guide' : 'What do these mean?'}
      </button>
      {helpOpen && (
        <ul
          className={`max-w-xl space-y-1 ${mutedCls} ${alignText} list-none pl-0`}
          data-testid="cofounder-autonomy-help"
        >
          {AUTONOMY_MODES.map((row) => (
            <li key={row.value}>
              <span className={`font-extrabold ${variant === 'light' ? 'text-[#16130d]' : 'text-[#fbbf0f]'}`}>
                {row.option}:
              </span>{' '}
              {row.summary}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** One primary CTA: Launch Brand | Resume | Open (home) / Pause (workspace when running). */
const PrimarySessionCta = ({
  company,
  busy,
  context = 'home', // home | workspace
  onLaunchBrand,
  onResume,
  onOpen,
  onPause,
}) => {
  const status = company?.status;
  const killed = Boolean(company?.kill_switch);
  if (context === 'workspace' && status === 'running') {
    return (
      <button
        type="button"
        onClick={onPause}
        disabled={busy}
        data-testid="cofounder-primary-cta"
        className={btnDark}
        title="Pause the running session"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
        Pause
      </button>
    );
  }
  if (status === 'running') {
    return (
      <button
        type="button"
        onClick={onOpen}
        disabled={busy}
        data-testid="cofounder-primary-cta"
        className={btnPrimary}
        title="Open the live workspace"
      >
        <Play className="h-4 w-4" />
        Open
      </button>
    );
  }
  if (status === 'paused') {
    return (
      <button
        type="button"
        onClick={onResume}
        disabled={busy || killed}
        data-testid="cofounder-primary-cta"
        className={btnPrimary}
        title="Resume the paused session"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
        Resume
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onLaunchBrand}
      disabled={busy || killed}
      data-testid="cofounder-primary-cta"
      className={btnPrimary}
      title="Start Launch Brand with current autonomy"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
      Launch Brand
    </button>
  );
};

/** Blank-slate Idea Lab — generate 3–5 concepts, then Guided create or Autonomous launch. */
const IdeaLabPanel = ({ onCompanyReady, newCompany = true, compact = false, fillHeight = false }) => {
  const [form, setForm] = useState({
    industry: '', skills: '', budget: '', city: '', freeform: '',
  });
  const [ideas, setIdeas] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [source, setSource] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(!compact);

  const generate = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr(''); setIdeas([]); setSelectedId('');
    try {
      const res = await fetch(`${API_URL}/api/cofounder/ideas`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not generate ideas');
      setIdeas(data.ideas || []);
      setBatchId(data.batch_id || '');
      setSource(data.source || '');
      if (!(data.ideas || []).length) setErr('No ideas returned — try freer text.');
    } catch (e2) {
      setErr(e2.message || 'Idea Lab failed');
    } finally { setBusy(false); }
  };

  const pick = async (mode) => {
    const idea = ideas.find((i) => i.id === selectedId) || ideas[0];
    if (!idea) { setErr('Pick a concept first.'); return; }
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/cofounder/ideas/select`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          idea_id: idea.id,
          batch_id: batchId,
          idea,
          mode,
          auto_start: mode === 'autonomous',
          new_company: newCompany,
          autonomy_level: mode === 'autonomous' ? 'L2' : 'L1',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not create company from idea');
      toast.success(mode === 'autonomous' ? 'Company created — Launch Brand started' : 'Company created');
      onCompanyReady?.(data);
    } catch (e2) {
      setErr(typeof e2.message === 'string' ? e2.message : 'Select failed');
      toast.error('Could not use this idea');
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`min-w-0 h-full min-h-full flex flex-col ${card} p-5 sm:p-6 ${fillHeight ? 'md:min-h-[560px]' : ''}`}
      data-testid="cofounder-idea-lab"
      style={fillHeight ? { minHeight: 560, height: '100%' } : undefined}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left shrink-0"
        data-testid="cofounder-idea-lab-toggle"
      >
        <div>
          <div className="text-xs font-extrabold uppercase tracking-wide text-[#fbbf0f]">Idea Lab</div>
          <p className="text-[11px] text-[#c4b8a5] mt-0.5 font-medium">
            No brief yet? Generate 3–5 concepts from light inputs, then create or launch.
          </p>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-[#fbbf0f] shrink-0" /> : <ChevronRight className="h-4 w-4 text-[#fbbf0f] shrink-0" />}
      </button>

      {open ? (
        <div className="flex flex-col flex-1 min-h-0 mt-3 gap-3">
          <form
            onSubmit={generate}
            className="flex flex-col flex-1 min-h-0 gap-2"
          >
            <div className="grid sm:grid-cols-2 gap-2">
              <input
                className={inputCls}
                placeholder="Industry (or leave blank)"
                value={form.industry}
                onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
                data-testid="cofounder-idea-industry"
              />
              <input
                className={inputCls}
                placeholder="Skills / strengths"
                value={form.skills}
                onChange={(e) => setForm((f) => ({ ...f, skills: e.target.value }))}
                data-testid="cofounder-idea-skills"
              />
              <input
                className={inputCls}
                placeholder="Budget"
                value={form.budget}
                onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                data-testid="cofounder-idea-budget"
              />
              <input
                className={inputCls}
                placeholder="City"
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                data-testid="cofounder-idea-city"
              />
              <textarea
                className={`${inputCls} sm:col-span-2 min-h-[72px]`}
                placeholder='Freeform — e.g. "I don’t know what to start"'
                value={form.freeform}
                onChange={(e) => setForm((f) => ({ ...f, freeform: e.target.value }))}
                data-testid="cofounder-idea-freeform"
              />
            </div>
            <div className="mt-auto pt-2">
              <button
                type="submit"
                disabled={busy}
                className={`${btnPrimary} w-full`}
                data-testid="cofounder-idea-generate"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Generate ideas
              </button>
            </div>
          </form>

          {err && <p className="text-xs text-red-300 font-medium" data-testid="cofounder-idea-error">{err}</p>}
          {source && ideas.length > 0 && (
            <p className="text-[10px] text-[#8a8072] font-medium">Source: {source}</p>
          )}

          {ideas.length > 0 && (
            <div className="space-y-2" data-testid="cofounder-idea-results">
              {ideas.map((idea) => {
                const on = selectedId === idea.id;
                return (
                  <button
                    key={idea.id}
                    type="button"
                    onClick={() => setSelectedId(idea.id)}
                    className={`w-full text-left rounded-xl border px-3 py-2.5 transition ${
                      on
                        ? 'border-[#16130d] bg-[#fbbf0f] text-[#16130d]'
                        : 'border-white/10 bg-[#0e0c0a] text-[#e4d9c6] hover:border-[#fbbf0f]/40'
                    }`}
                    data-testid={`cofounder-idea-card-${idea.id}`}
                  >
                    <div className={`text-sm font-extrabold ${on ? 'text-[#16130d]' : 'text-white'}`}>{idea.name}</div>
                    <div className={`text-xs mt-0.5 font-medium ${on ? 'text-[#3a3327]' : 'text-[#c4b8a5]'}`}>{idea.one_liner}</div>
                    {(idea.icp || idea.offer) && (
                      <div className={`text-[11px] mt-1.5 space-y-0.5 font-medium ${on ? 'text-[#3a3327]' : 'text-[#a89882]'}`}>
                        {idea.icp ? <div>ICP: {idea.icp}</div> : null}
                        {idea.offer ? <div>Offer: {idea.offer}</div> : null}
                        {idea.why_now ? <div>Why now: {idea.why_now}</div> : null}
                      </div>
                    )}
                  </button>
                );
              })}
              <div className="mt-auto flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  disabled={busy || !selectedId}
                  onClick={() => pick('guided')}
                  className={btnDark}
                  data-testid="cofounder-idea-guided"
                  title="Create company only — you launch when ready"
                >
                  Guided — create
                </button>
                <button
                  type="button"
                  disabled={busy || !selectedId}
                  onClick={() => pick('autonomous')}
                  className={btnPrimary}
                  data-testid="cofounder-idea-autonomous"
                  title="Create company and start Launch Brand"
                >
                  <Rocket className="h-4 w-4" />
                  Autonomous — launch
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0" aria-hidden="true" />
      )}
    </div>
  );
};

const CreateCompanyForm = ({ onCreated, navigate, onCancel, newCompany = false }) => {
  const [form, setForm] = useState({ name: '', description: '', goals: '', target_audience: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim()) { setErr('Give your company a name and a short description.'); return; }
    setSaving(true); setErr('');
    try {
      const res = await fetch(`${API_URL}/api/cofounder/company`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...form, new_company: newCompany }),
      });
      if (!res.ok) throw new Error('Failed to create company');
      onCreated(await res.json());
    } catch (e2) { setErr(e2.message || 'Something went wrong'); } finally { setSaving(false); }
  };

  return (
    <form
      onSubmit={submit}
      className={`min-w-0 w-full h-full min-h-full flex flex-col ${card} p-5 sm:p-6 space-y-4 md:min-h-[560px]`}
      data-testid="cofounder-onboarding-form"
      style={{ minHeight: 560, height: '100%' }}
    >
      <div className="space-y-2 shrink-0">
        <div className="w-12 h-12 rounded-2xl bg-[#fbbf0f] border-2 border-[#16130d] shadow-[0_4px_0_0_#16130d] flex items-center justify-center">
          <Brain className="h-6 w-6 text-[#16130d]" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-sans font-semibold text-white tracking-normal" style={{ fontFamily: "'IBM Plex Sans', Manrope, sans-serif" }}>
          {newCompany ? 'New company' : 'Create your company'}
        </h1>
        <p className="text-sm text-[#c4b8a5]">
          Know the brief? Fill this in — or use Idea Lab if you&apos;re still exploring.{' '}
          <button type="button" onClick={() => navigate(GUIDE_PATH)} className="db-link underline-offset-2 hover:underline" data-testid="cofounder-howto">
            How it works
          </button>
        </p>
      </div>
      <div className="shrink-0">
        <label className="block text-sm font-bold text-[#f5f0e6] mb-1.5">Company name</label>
        <input data-testid="cofounder-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. BrewBuddy" className={inputCls} />
      </div>
      <div className="shrink-0">
        <label className="block text-sm font-bold text-[#f5f0e6] mb-1.5">What does it do?</label>
        <textarea data-testid="cofounder-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3} placeholder="A coffee shop based in Austin…" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-3 shrink-0">
        <div>
          <label className="block text-xs font-bold text-[#c4b8a5] mb-1.5">Goal</label>
          <input value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })}
            placeholder="Optional" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-bold text-[#c4b8a5] mb-1.5">Audience</label>
          <input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
            placeholder="Optional" className={inputCls} />
        </div>
      </div>
      {err && <p className="text-sm text-red-300 font-medium shrink-0" data-testid="cofounder-error">{err}</p>}
      <div className="mt-auto space-y-2 pt-2">
        <button type="submit" disabled={saving} data-testid="cofounder-create-button" className={`w-full ${btnPrimary}`}>
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Rocket className="h-5 w-5" />}
          {saving ? 'Creating…' : 'Create'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="w-full text-sm font-semibold text-[#c4b8a5] hover:text-white py-1">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

const SessionPicker = ({
  company, sessions, companies, navigate, busy, readiness,
  onOpenSession, onOpenLive, onNewSession, onNewCompany, onActivateCompany,
  onPatchAutonomy, onToggleKillSwitch, onLaunchBrand, onResume,
  onIdeaCompanyReady,
}) => (
  <div className="max-w-xl mx-auto space-y-6" data-testid="cofounder-session-picker">
    <div className={`${cardYellow} px-5 py-6 text-center space-y-3`}>
      <h1 className="text-2xl sm:text-3xl font-sans font-semibold tracking-normal" style={{ fontFamily: "'IBM Plex Sans', Manrope, sans-serif" }}>AI Co-founder</h1>
      <p className="text-sm font-medium text-[#3a3327]">
        Choose Guided or Autonomous, then launch — or start from Idea Lab below.
      </p>
      {company && (
        <AutonomyControls
          autonomyLevel={company.autonomy_level || 'L1'}
          killSwitch={Boolean(company.kill_switch)}
          busy={busy}
          onPatchAutonomy={onPatchAutonomy}
          onToggleKillSwitch={onToggleKillSwitch}
          variant="light"
        />
      )}
      {company && (
        <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
          <PrimarySessionCta
            company={company}
            busy={busy}
            context="home"
            onLaunchBrand={onLaunchBrand}
            onResume={onResume}
            onOpen={onOpenLive}
          />
        </div>
      )}
      <button
        type="button"
        onClick={() => navigate(GUIDE_PATH)}
        className="inline-flex items-center gap-1.5 text-sm font-extrabold text-[#16130d] underline underline-offset-2"
        data-testid="cofounder-howto"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        How AI Co-founder works (Platform Guide)
      </button>
    </div>

    <SuccessChecklist readiness={readiness} navigate={navigate} />

    <IdeaLabPanel
      compact
      newCompany
      onCompanyReady={onIdeaCompanyReady}
    />

    {company && (
      <button
        type="button"
        onClick={onOpenLive}
        disabled={busy}
        className={`w-full text-left ${card} hover:border-[#fbbf0f]/50 px-5 py-4 transition disabled:opacity-50`}
        data-testid="cofounder-open-current"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-extrabold text-[#fbbf0f] uppercase tracking-wide mb-1">Current</div>
            <div className="text-lg font-sans font-semibold text-white truncate" style={{ fontFamily: "'IBM Plex Sans', Manrope, sans-serif" }}>{company.name}</div>
            <div className="text-xs text-[#c4b8a5] mt-0.5 capitalize font-medium flex items-center gap-2">
              <StatusPill status={company.status || 'stopped'} killSwitch={Boolean(company.kill_switch)} />
              <span>{autonomyLabel(company.autonomy_level)}</span>
            </div>
          </div>
          <span className="shrink-0 w-10 h-10 rounded-full bg-[#fbbf0f] border-2 border-[#16130d] shadow-[0_3px_0_0_#16130d] flex items-center justify-center">
            <Play className="h-4 w-4 text-[#16130d]" />
          </span>
        </div>
      </button>
    )}

    <div className={`${card} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-sans font-semibold uppercase tracking-wider text-[#fbbf0f]" style={{ fontFamily: "'IBM Plex Sans', Manrope, sans-serif" }}>Sessions</h2>
        {company && (
          <button type="button" disabled={busy} onClick={onNewSession}
            className="text-xs font-extrabold text-[#fbbf0f] hover:text-[#ffd84a]" data-testid="cofounder-new-session">
            + New session
          </button>
        )}
      </div>
      {sessions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 px-4 py-8 text-center text-sm text-[#c4b8a5]">
          No saved sessions yet. Open current and press Launch.
        </div>
      ) : (
        <div className="space-y-2" data-testid="cofounder-sessions-panel">
          {sessions.map((s) => (
            <div
              key={s.id}
              className="rounded-xl border border-white/10 bg-[#0e0c0a] hover:border-[#fbbf0f]/40 px-4 py-3 flex items-center gap-3"
              data-testid={`cofounder-session-${s.id}`}
            >
              <button type="button" onClick={() => onOpenSession(s.id)} className="flex-1 text-left min-w-0" disabled={busy}>
                <div className="text-sm font-bold text-white truncate">{s.label || 'Session'}</div>
                <div className="text-[11px] text-[#a89882] mt-0.5 font-medium">
                  {s.status} · {timeAgo(s.started_at)}
                  {s.stats?.actions_executed != null ? ` · ${s.stats.actions_executed} actions` : ''}
                </div>
              </button>
              <button
                type="button"
                onClick={() => navigate(outputsPath(s.id))}
                className="text-xs font-extrabold text-[#fbbf0f] shrink-0"
                data-testid={`cofounder-session-outputs-${s.id}`}
              >
                Results
              </button>
            </div>
          ))}
        </div>
      )}
    </div>

    {companies.length > 1 && (
      <div className={`${card} p-4`}>
        <h2 className="text-xs font-extrabold uppercase tracking-wide text-[#fbbf0f] mb-3">Companies</h2>
        <div className="space-y-1.5">
          {companies.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={busy || c.id === company?.id}
              onClick={() => onActivateCompany(c.id)}
              className={`w-full text-left rounded-xl px-3 py-2.5 text-sm font-semibold border ${
                c.id === company?.id
                  ? 'border-[#16130d] bg-[#fbbf0f] text-[#16130d]'
                  : 'border-white/10 bg-[#0e0c0a] text-[#e4d9c6] hover:border-[#fbbf0f]/40'
              }`}
            >
              {c.name}
              {c.is_active ? <span className="text-[11px] opacity-70 ml-2">active</span> : null}
            </button>
          ))}
        </div>
      </div>
    )}

    <button
      type="button"
      onClick={onNewCompany}
      className={`w-full ${ghostChip} justify-center py-2.5`}
      data-testid="cofounder-new-company"
    >
      + Create another company
    </button>
  </div>
);

const TaskCard = ({ task, onOpen, navigate }) => {
  const m = roleMeta(task.role);
  const Icon = m.icon;
  const statusIcon = task.status === 'done' ? <CheckCircle2 className="h-4 w-4 text-[#fbbf0f]" />
    : task.status === 'needs_approval' ? <ShieldAlert className="h-4 w-4 text-[#fbbf0f]" />
    : task.status === 'in_progress' ? <Loader2 className="h-4 w-4 text-[#fbbf0f] animate-spin" />
    : <Clock className="h-4 w-4 text-[#8a8072]" />;
  const link = task.deep_link || task.deliverable?.deep_link;
  return (
    <div data-testid={`cofounder-task-${task.id}`} className={`${card} p-4`}>
      <button type="button" onClick={() => task.deliverable && onOpen(task)} className={`w-full text-left ${task.deliverable ? 'cursor-pointer' : 'cursor-default'}`}>
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-xl bg-[#fbbf0f] border-2 border-[#16130d] shadow-[0_2px_0_0_#16130d] p-2 text-[#16130d]">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-extrabold uppercase tracking-wide text-[#fbbf0f]">{m.label}</span>
              {statusIcon}
            </div>
            <div className="text-sm font-bold text-white mt-0.5 truncate">{task.title}</div>
            {task.status === 'needs_approval' && (
              <div className="text-xs font-bold text-[#fbbf0f] mt-1">Needs your approval</div>
            )}
            {(task.status === 'done' || task.status === 'needs_approval') && task.deliverable?.summary && (
              <div className="text-xs text-[#c4b8a5] mt-1 line-clamp-2">{stripMarkdown(task.deliverable.summary)}</div>
            )}
          </div>
        </div>
      </button>
      {link && (
        <button
          type="button"
          onClick={() => navigate(link)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-extrabold text-[#fbbf0f] hover:text-[#ffd84a]"
          data-testid={`cofounder-task-link-${task.id}`}
        >
          <ExternalLink className="h-3.5 w-3.5" /> Open in platform
        </button>
      )}
    </div>
  );
};

const ActivityRow = ({ ev, navigate }) => {
  const m = roleMeta(ev.role);
  const Icon = ev.type === 'task_done' || ev.type === 'action_executed' ? CheckCircle2
    : ev.type === 'deliverable' ? Sparkles
    : ev.type === 'digest' || ev.type === 'weekly_report' ? Activity
    : ev.type === 'action_proposed' ? ShieldAlert
    : ev.type === 'task_started' ? Loader2
    : ev.type === 'error' ? X : m.icon;
  const tone = ev.type === 'error' ? 'text-red-300' : 'text-[#fbbf0f]';
  return (
    <div className="flex gap-3 py-3 border-b border-white/8 last:border-0" data-testid="cofounder-activity-row">
      <div className={`shrink-0 mt-0.5 ${tone}`}>
        <Icon className={`h-4 w-4 ${ev.type === 'task_started' ? 'animate-spin' : ''}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-white">{ev.title}</div>
        {ev.detail && <div className="text-xs text-[#c4b8a5] mt-0.5 line-clamp-3 whitespace-pre-wrap">{ev.detail}</div>}
        {ev.deep_link && (
          <button type="button" onClick={() => navigate(ev.deep_link)}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-extrabold text-[#fbbf0f] hover:text-[#ffd84a]">
            <ExternalLink className="h-3 w-3" /> Open
          </button>
        )}
      </div>
      <div className="shrink-0 text-[11px] font-medium text-[#8a8072]">{timeAgo(ev.created_at)}</div>
    </div>
  );
};

const DeliverableModal = ({ task, onClose, navigate }) => {
  if (!task) return null;
  const m = roleMeta(task.role);
  const link = task.deep_link || task.deliverable?.deep_link;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={onClose} data-testid="cofounder-deliverable-modal">
      <div className={`w-full max-w-2xl max-h-[80vh] overflow-hidden ${card} flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#fbbf0f]/20 bg-[#fbbf0f]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] font-extrabold uppercase tracking-wide text-[#16130d]">{m.label}</span>
            <span className="text-[#16130d] font-extrabold truncate">{task.deliverable?.title || task.title}</span>
          </div>
          <button onClick={onClose} className="text-[#16130d] hover:opacity-70"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto bg-[#1c1914]">
          {task.deliverable?.summary && <p className="text-sm text-[#c4b8a5] mb-3">{stripMarkdown(task.deliverable.summary)}</p>}
          {link && (
            <button type="button" onClick={() => { navigate(link); onClose(); }}
              className={`mb-4 ${btnPrimary} text-xs px-3 py-2`}>
              <ExternalLink className="h-3.5 w-3.5" /> Open result in platform
            </button>
          )}
          <pre className="whitespace-pre-wrap text-sm text-[#f5f0e6] font-sans leading-relaxed">{stripMarkdown(task.deliverable?.content)}</pre>
        </div>
      </div>
    </div>
  );
};

const AICofounderDashboard = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState('home'); // home | workspace | create
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [planLimits, setPlanLimits] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [proposed, setProposed] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [viewingSessionId, setViewingSessionId] = useState(null);
  const [sessionReadOnly, setSessionReadOnly] = useState(false);
  const [panel, setPanel] = useState('activity'); // activity | tasks | chat
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  const [decidingAction, setDecidingAction] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const pollRef = useRef(null);
  const sseRef = useRef(null);
  const chatEndRef = useRef(null);
  const pendingActionIdsRef = useRef(new Set());
  /** Skip overlapping workspace refreshes (poll + visibility + SSE fallback). */
  const workspaceRefreshInFlightRef = useRef(false);
  /** One-shot: remount after Outputs/Back should reopen a live session, not strand on the picker. */
  const autoResumeWorkspaceRef = useRef(true);

  const loadReadiness = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cofounder/readiness`, { headers: authHeaders() });
      if (!res.ok) return;
      setReadiness(await res.json());
    } catch { /* ignore */ }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cofounder/sessions?limit=40`, { headers: authHeaders() });
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch { /* ignore */ }
  }, []);

  const loadWorkspaceData = useCallback(async ({ preserveProposedIds = [] } = {}) => {
    const [rRes, aRes, actRes, cRes] = await Promise.all([
      fetch(`${API_URL}/api/cofounder/roadmap`, { headers: authHeaders() }),
      fetch(`${API_URL}/api/cofounder/activity?limit=80`, { headers: authHeaders() }),
      fetch(`${API_URL}/api/cofounder/actions?status=proposed&limit=20`, { headers: authHeaders() }),
      fetch(`${API_URL}/api/cofounder/chat`, { headers: authHeaders() }),
    ]);
    setTasks((await rRes.json()).tasks || []);
    setActivity((await aRes.json()).activity || []);
    const actions = (await actRes.json()).actions || [];
    const preservedIds = new Set([
      ...preserveProposedIds,
      ...pendingActionIdsRef.current,
    ].map((id) => String(id)));
    setProposed(actions.filter((action) => !preservedIds.has(String(action.id))));
    setMessages((await cRes.json()).messages || []);
  }, []);

  /** Live workspace refresh: company status/stats + decision inbox + tasks/activity. */
  const refreshLiveWorkspace = useCallback(async () => {
    if (workspaceRefreshInFlightRef.current) return;
    workspaceRefreshInFlightRef.current = true;
    try {
      const coRes = await fetch(`${API_URL}/api/cofounder/company`, { headers: authHeaders() });
      if (coRes.ok) {
        const data = await coRes.json();
        if (data.company) {
          setCompany(data.company);
          setPlanLimits(data.plan_limits || data.company?.plan_limits || null);
        }
      }
      await loadWorkspaceData();
    } catch { /* ignore transient poll errors */ } finally {
      workspaceRefreshInFlightRef.current = false;
    }
  }, [loadWorkspaceData]);

  const load = useCallback(async () => {
    try {
      const [coRes, listRes] = await Promise.all([
        fetch(`${API_URL}/api/cofounder/company`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/cofounder/companies`, { headers: authHeaders() }),
      ]);
      const data = await coRes.json();
      const listData = await listRes.json().catch(() => ({ companies: [] }));
      setCompanies(listData.companies || []);
      setCompany(data.company || null);
      setPlanLimits(data.plan_limits || data.company?.plan_limits || null);
      loadReadiness();
      if (data.company) {
        setViewingSessionId(data.company.active_session_id || null);
        setSessionReadOnly(false);
        await loadSessions();
        const liveSession = Boolean(data.company.active_session_id)
          && ['running', 'paused'].includes(data.company.status);
        if (autoResumeWorkspaceRef.current && liveSession) {
          try {
            await loadWorkspaceData();
            setScreen('workspace');
            autoResumeWorkspaceRef.current = false;
          } catch {
            // Keep resume armed so a later load (or Back from Outputs) can retry.
            setScreen('home');
          }
        } else {
          setScreen((prev) => (prev === 'workspace' ? 'workspace' : 'home'));
        }
      } else {
        setScreen('create');
        setSessions([]);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [loadSessions, loadWorkspaceData, loadReadiness]);

  const enterWorkspace = async () => {
    setBusy(true);
    try {
      await loadWorkspaceData();
      loadReadiness();
      setSessionReadOnly(false);
      setScreen('workspace');
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  // Outputs "Back" / Chat link passes resumeWorkspace so we reopen the live session, not the picker.
  useEffect(() => {
    if (!location.state?.resumeWorkspace || !company?.active_session_id) return undefined;
    const openChat = Boolean(location.state?.openChat);
    let cancelled = false;
    (async () => {
      await enterWorkspace();
      if (!cancelled && openChat) setPanel('chat');
      if (!cancelled) navigate(location.pathname, { replace: true, state: {} });
    })();
    return () => { cancelled = true; };
    // intentionally only when navigation state asks to resume
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.resumeWorkspace, location.state?.openChat, company?.active_session_id]);

  const openSession = async (sessionId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/cofounder/sessions/${sessionId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load session');
      const data = await res.json();
      setViewingSessionId(sessionId);
      setSessionReadOnly(Boolean(data.read_only));
      setTasks(data.tasks || []);
      setActivity(data.activity || []);
      setMessages(data.messages || []);
      setProposed([]);
      if (data.company) {
        setCompany((prev) => ({
          ...(prev || {}),
          ...data.company,
          stats: data.session?.stats || data.company.stats || prev?.stats,
        }));
      }
      setScreen('workspace');
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  const startNewSession = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/cofounder/sessions/new`, { method: 'POST', headers: authHeaders() });
      await load();
      await enterWorkspace();
    } finally { setBusy(false); }
  };

  const activateCompany = async (companyId) => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/cofounder/companies/${companyId}/activate`, {
        method: 'POST', headers: authHeaders(),
      });
      await load();
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [load]);

  // SSE for snappy updates + REST poll so decision inbox / tasks stay fresh while live.
  // Prior gap: poll called load() (no workspace refetch after first resume) and only when running;
  // SSE also skips proposed-action pushes unless activity id churns — so Approve all stayed stale.
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    if (!company || sessionReadOnly || screen !== 'workspace') return undefined;
    const live = ['running', 'paused'].includes(company.status);
    if (!live) return undefined;

    const WORKSPACE_POLL_MS = 7000;

    const token = (localStorage.getItem('token') || '').trim();
    if (token && typeof EventSource !== 'undefined') {
      try {
        const url = `${API_URL}/api/cofounder/events?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        sseRef.current = es;
        es.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data || '{}');
            if (data.type === 'ping' && data.status) {
              setCompany((prev) => (prev && data.status !== prev.status
                ? { ...prev, status: data.status }
                : prev));
              return;
            }
            if (data.type === 'snapshot' || data.type === 'update') {
              setCompany((prev) => (prev ? {
                ...prev,
                status: data.status || prev.status,
                stats: data.stats || prev.stats,
              } : prev));
              if (Array.isArray(data.activity)) setActivity(data.activity);
              if (Array.isArray(data.actions_proposed)) {
                setProposed(data.actions_proposed.filter(
                  (action) => !pendingActionIdsRef.current.has(String(action.id)),
                ));
              }
              // Refresh roadmap occasionally on updates (lighter than full poll)
              if (data.type === 'update') {
                fetch(`${API_URL}/api/cofounder/roadmap`, { headers: authHeaders() })
                  .then((r) => r.json())
                  .then((j) => setTasks(j.tasks || []))
                  .catch(() => {});
              }
            }
          } catch { /* ignore bad frames */ }
        };
        es.onerror = () => {
          es.close();
          sseRef.current = null;
        };
      } catch { /* EventSource unavailable — poll covers it */ }
    }

    // Always poll while workspace is live (running or paused awaiting approval).
    pollRef.current = setInterval(() => { refreshLiveWorkspace(); }, WORKSPACE_POLL_MS);

    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
    };
  }, [company?.id, company?.status, refreshLiveWorkspace, sessionReadOnly, screen]);

  // Refresh when the tab becomes visible again (missed SSE/poll while backgrounded).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (screen !== 'workspace' || sessionReadOnly) return;
      if (!company || !['running', 'paused'].includes(company.status)) return;
      refreshLiveWorkspace();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [company, screen, sessionReadOnly, refreshLiveWorkspace]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const start = async (opts = {}) => {
    setBusy(true);
    try {
      const body = {};
      if (opts.pipeline) body.pipeline = opts.pipeline;
      if (opts.autonomy_level) body.autonomy_level = opts.autonomy_level;
      const res = await fetch(`${API_URL}/api/cofounder/start`, {
        method: 'POST',
        headers: authHeaders(),
        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (data.plan_limits) setPlanLimits(data.plan_limits);
      if (data.company) setCompany(data.company);
      await load();
      await loadWorkspaceData();
      setScreen('workspace');
    } finally { setBusy(false); }
  };
  const pause = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/cofounder/pause`, { method: 'POST', headers: authHeaders() });
      await load();
      setScreen('home');
    } finally { setBusy(false); }
  };

  const decideAction = async (id, decision) => {
    setDecidingId(id);
    setDecidingAction(decision);
    pendingActionIdsRef.current.add(String(id));
    setProposed((prev) => prev.filter((action) => String(action.id) !== String(id)));
    try {
      const res = await fetch(`${API_URL}/api/cofounder/actions/${id}/${decision}`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to ${decision} action`);
      await load();
      await loadWorkspaceData({ preserveProposedIds: [id] });
      pendingActionIdsRef.current.delete(String(id));
      toast.success(decision === 'approve' ? 'Approved — sending' : 'Rejected — kept as draft');
    } catch (err) {
      pendingActionIdsRef.current.delete(String(id));
      await loadWorkspaceData();
      toast.error(err.message || 'Could not update this approval');
    } finally {
      setDecidingId(null);
      setDecidingAction(null);
    }
  };

  const approveAllActions = async () => {
    const actionIds = proposed.map((action) => action.id);
    setApprovingAll(true);
    actionIds.forEach((id) => pendingActionIdsRef.current.add(String(id)));
    setProposed([]);
    try {
      const res = await fetch(`${API_URL}/api/cofounder/actions/approve-all`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to approve all actions');
      await load();
      await loadWorkspaceData({ preserveProposedIds: actionIds });
      actionIds.forEach((id) => pendingActionIdsRef.current.delete(String(id)));
      toast.success('All approved');
    } catch (err) {
      actionIds.forEach((id) => pendingActionIdsRef.current.delete(String(id)));
      await loadWorkspaceData();
      toast.error(err.message || 'Could not approve all actions');
    } finally { setApprovingAll(false); }
  };

  const patchAutonomy = async (autonomy_level) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/cofounder/company/settings`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ autonomy_level }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.company) setCompany(data.company);
      else await load();
    } finally { setBusy(false); }
  };

  const toggleKillSwitch = async (enabled) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/cofounder/kill-switch`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ enabled: Boolean(enabled) }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.company) setCompany(data.company);
      else await load();
    } finally { setBusy(false); }
  };

  const rollbackAction = async (id) => {
    setDecidingId(id);
    setDecidingAction('rollback');
    pendingActionIdsRef.current.add(String(id));
    setProposed((prev) => prev.filter((action) => String(action.id) !== String(id)));
    try {
      const res = await fetch(`${API_URL}/api/cofounder/actions/${id}/rollback`, {
        method: 'POST', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to roll back action');
      await load();
      await loadWorkspaceData({ preserveProposedIds: [id] });
      pendingActionIdsRef.current.delete(String(id));
      toast.success('Rolled back');
    } catch (err) {
      pendingActionIdsRef.current.delete(String(id));
      await loadWorkspaceData();
      toast.error(err.message || 'Could not roll back this action');
    } finally {
      setDecidingId(null);
      setDecidingAction(null);
    }
  };

  const sendChat = async (e) => {
    e?.preventDefault?.();
    const text = chatInput.trim();
    if (!text || chatBusy || sessionReadOnly) return;
    setChatBusy(true);
    setChatInput('');
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString() }]);
    try {
      const res = await fetch(`${API_URL}/api/cofounder/chat`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ message: text }),
      });
      const data = await res.json();
      if (data.reply) {
        setMessages((prev) => [...prev, { id: data.messages?.[1]?.id || `a-${Date.now()}`, role: 'assistant', content: data.reply, created_at: new Date().toISOString() }]);
      }
      await load();
    } catch {
      setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: 'assistant', content: 'Chat failed — is the backend running?', created_at: new Date().toISOString() }]);
    } finally { setChatBusy(false); }
  };

  const backToLive = async () => {
    setSessionReadOnly(false);
    await load();
    await enterWorkspace();
  };

  const onCompanyCreated = async () => {
    await load();
    await enterWorkspace();
  };

  const onIdeaCompanyReady = async (data) => {
    autoResumeWorkspaceRef.current = true;
    if (data?.company) setCompany(data.company);
    await load();
    if (data?.auto_started || data?.started) {
      await loadWorkspaceData();
      setScreen('workspace');
    } else {
      await enterWorkspace();
    }
  };

  const headerExtras = (
    <div className="flex items-center gap-2">
      {screen === 'workspace' && (
        <button type="button" onClick={() => setScreen('home')} className={ghostChip} data-testid="cofounder-sessions-toggle">
          <History className="h-3.5 w-3.5" /> Sessions
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <DashboardShell user={user} fullscreen title="AI Co-founder" subtitle="Autonomous company OS" activeNav="ai-cofounder" backPath={DASHBOARD_URL}>
        <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#fbbf0f]" /></div>
      </DashboardShell>
    );
  }

  // Landing: session picker (or create form only when no company yet)
  if (screen !== 'workspace') {
    return (
      <DashboardShell
        user={user}
        fullscreen
        title="AI Co-founder"
        subtitle={company ? 'Choose a session' : 'Get started'}
        activeNav="ai-cofounder"
        backPath={DASHBOARD_URL}
        headerExtras={headerExtras}
      >
        <div className={`mx-auto w-full px-4 sm:px-6 py-2 pb-24 sm:pb-6 ${(screen === 'create' || !company) ? 'max-w-5xl' : 'max-w-3xl'}`} data-testid="cofounder-page">
          <ModuleDemoVideo moduleKey="ai-cofounder" className="mb-4" />
          {screen === 'create' || !company ? (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full items-stretch cofounder-onboarding-paths"
              data-testid="cofounder-onboarding-paths"
              style={{ alignItems: 'stretch' }}
            >
              <div className="min-w-0 h-full flex self-stretch" style={{ minHeight: '100%' }}>
                <CreateCompanyForm
                  navigate={navigate}
                  newCompany={Boolean(company)}
                  onCreated={onCompanyCreated}
                  onCancel={company ? () => setScreen('home') : undefined}
                />
              </div>
              <div className="min-w-0 h-full flex self-stretch" style={{ minHeight: '100%' }}>
                <IdeaLabPanel
                  fillHeight
                  newCompany={Boolean(company) || !company}
                  onCompanyReady={onIdeaCompanyReady}
                />
              </div>
            </div>
          ) : (
            <SessionPicker
              company={company}
              companies={companies}
              sessions={sessions}
              navigate={navigate}
              busy={busy}
              readiness={readiness}
              onOpenLive={enterWorkspace}
              onOpenSession={openSession}
              onNewSession={startNewSession}
              onActivateCompany={activateCompany}
              onNewCompany={() => setScreen('create')}
              onPatchAutonomy={patchAutonomy}
              onToggleKillSwitch={toggleKillSwitch}
              onIdeaCompanyReady={onIdeaCompanyReady}
              onLaunchBrand={() => start({
                pipeline: 'launch_brand',
                autonomy_level: company?.autonomy_level || 'L1',
              })}
              onResume={() => start({
                autonomy_level: company?.autonomy_level || 'L1',
              })}
            />
          )}
        </div>
      </DashboardShell>
    );
  }

  const done = company?.stats?.tasks_done || 0;
  const total = company?.stats?.tasks_total || 0;
  const waiting = company?.stats?.actions_proposed || proposed.length || 0;
  const stuckTasks = (tasks || []).filter((t) => t.status === 'in_progress');
  const stuckNeedsApproval = (tasks || []).filter((t) => t.status === 'needs_approval');

  return (
    <DashboardShell
      user={user}
      fullscreen
      title="AI Co-founder"
      subtitle={company ? company.name : 'Get started'}
      activeNav="ai-cofounder"
      backPath={DASHBOARD_URL}
      headerExtras={headerExtras}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-24 sm:pb-6 space-y-5" data-testid="cofounder-page">
        <ModuleDemoVideo moduleKey="ai-cofounder" className="mb-0" />
        <div className={`${cardYellow} p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-sans font-semibold tracking-normal truncate" style={{ fontFamily: "'IBM Plex Sans', Manrope, sans-serif" }} data-testid="cofounder-company-name">
                {company.name}
              </h1>
              <StatusPill status={company.status} killSwitch={Boolean(company.kill_switch)} />
              {sessionReadOnly && (
                <span className="text-[11px] font-extrabold text-[#16130d]/70">Past session</span>
              )}
            </div>
            <p className="text-[#3a3327] text-sm mt-1 font-medium truncate max-w-xl">{company.description}</p>
            {planLimits && (
              <p className="text-xs font-bold text-[#16130d]/80 mt-2" data-testid="cofounder-plan-limits">
                Plan: <span className="capitalize">{planLimits.plan}</span>
                {' · '}
                {planLimits.mode === 'draft' && 'Teaser — plan & draft, 1 cycle/day'}
                {planLimits.mode === 'standard' && `≈${planLimits.cycles_per_day} cycles/day · approve before send`}
                {planLimits.mode === 'full' && `Always-on · up to ${planLimits.cycles_per_day} cycles/day · multi-brand`}
                {planLimits.mode === 'draft' && (
                  <>
                    {' · '}
                    <button type="button" onClick={() => navigate('/subscription-plans')} className="underline underline-offset-2 font-extrabold">
                      Upgrade for always-on
                    </button>
                  </>
                )}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm font-semibold text-[#16130d]">
              <span><span className="font-extrabold">{done}</span>/{total || '—'} tasks done</span>
              {waiting > 0 && <span className="font-extrabold">{waiting} waiting for you</span>}
              {stuckTasks.length > 0 && waiting === 0 && (
                <span className="font-extrabold text-[#8a5a00]" data-testid="cofounder-stuck-count">
                  {stuckTasks.length} in progress (no approval yet)
                </span>
              )}
              <button type="button" onClick={() => navigate(outputsPath(viewingSessionId || company.active_session_id))}
                className="inline-flex items-center gap-1 font-extrabold underline underline-offset-2" data-testid="cofounder-open-outputs">
                <LayoutGrid className="h-3.5 w-3.5" /> Results
              </button>
            </div>
            {!sessionReadOnly && (
              <div className="mt-3">
                <AutonomyControls
                  autonomyLevel={company.autonomy_level || 'L1'}
                  killSwitch={Boolean(company.kill_switch)}
                  busy={busy}
                  onPatchAutonomy={patchAutonomy}
                  onToggleKillSwitch={toggleKillSwitch}
                  variant="light"
                  align="start"
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {!sessionReadOnly ? (
              <PrimarySessionCta
                company={company}
                busy={busy}
                context="workspace"
                onLaunchBrand={() => start({
                  pipeline: 'launch_brand',
                  autonomy_level: company.autonomy_level || 'L1',
                })}
                onResume={() => start({
                  autonomy_level: company.autonomy_level || 'L1',
                })}
                onPause={pause}
              />
            ) : (
              <button onClick={backToLive} disabled={busy} data-testid="cofounder-back-live" className={btnDark}>
                Back to live
              </button>
            )}
          </div>
        </div>

        {!sessionReadOnly && readiness && (
          <SuccessChecklist readiness={readiness} navigate={navigate} compact />
        )}

        {!sessionReadOnly && proposed.length === 0 && stuckTasks.length > 0 && (
          <div className={`${card} border-[#fbbf0f]/40 p-4`} data-testid="cofounder-stuck-banner">
            <div className="text-sm font-extrabold text-[#fbbf0f]">Roadmap stuck — no approvals waiting</div>
            <p className="text-[11px] text-[#c4b8a5] mt-1 font-medium">
              {stuckTasks.length === 1 ? 'This task' : `${stuckTasks.length} tasks`} still show in progress
              without a decision item. The agent will auto-skip or re-queue orphans on the next tick.
            </p>
            <ul className="mt-2 space-y-1">
              {stuckTasks.slice(0, 3).map((t) => (
                <li key={t.id} className="text-xs text-white font-semibold truncate">
                  {(t.role || 'task')} · {t.title}
                  {t.deliverable?.summary ? ` — ${t.deliverable.summary}` : ''}
                </li>
              ))}
            </ul>
            {stuckNeedsApproval.length > 0 && (
              <p className="text-[11px] text-[#c4b8a5] mt-2 font-medium">
                {stuckNeedsApproval.length} task(s) marked needs_approval but no proposed action row — refresh or resume.
              </p>
            )}
          </div>
        )}

        {!sessionReadOnly && proposed.length > 0 && (
          <div className={`${card} border-[#fbbf0f]/40 p-4`} data-testid="cofounder-approvals">
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="text-sm font-extrabold text-[#fbbf0f]">Needs your OK before going live</div>
              {proposed.length > 1 && (
                <button
                  type="button"
                  disabled={busy || approvingAll || decidingId !== null}
                  onClick={approveAllActions}
                  className="rounded-full px-3 py-1 text-[11px] font-extrabold bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d] shrink-0"
                  data-testid="cofounder-approve-all"
                >
                  Approve all
                </button>
              )}
            </div>
            <p className="text-[11px] text-[#c4b8a5] mb-3 font-medium">Approve only if your email/social APIs are connected. Otherwise Reject and keep the draft.</p>
            <div className="space-y-2">
              {proposed.map((a) => (
                <div key={a.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl border border-white/10 bg-[#0e0c0a] px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm text-white font-bold capitalize">
                      {((a.payload && a.payload.title) || a.tool || '').replace(/_/g, ' ')}
                    </div>
                    <div className="text-[11px] text-[#a89882] font-medium flex items-center gap-2 flex-wrap">
                      <span>{roleMeta(a.role).label}</span>
                      {(a.source === 'loop' || (a.payload && a.payload.source === 'loop')) && (
                        <span
                          className="rounded-full border border-[#fbbf0f]/35 px-1.5 py-0.5 text-[10px] text-[#fbbf0f] font-bold"
                          data-testid={`cofounder-loop-badge-${a.id}`}
                        >
                          Business Loop
                        </span>
                      )}
                    </div>
                    {(a.rationale || a.policy_reason) && (
                      <div className="text-[11px] text-[#c4b8a5] mt-1 font-medium" data-testid={`cofounder-rationale-${a.id}`}>
                        {a.rationale || ''}{a.policy_reason ? ` · ${a.policy_reason}` : ''}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      disabled={busy || approvingAll || decidingId === a.id}
                      onClick={() => rollbackAction(a.id)}
                      className={`${ghostChip}`}
                      data-testid={`cofounder-rollback-${a.id}`}
                    >
                      {decidingId === a.id && decidingAction === 'rollback' && <Loader2 className="h-3 w-3 animate-spin" />}
                      Rollback
                    </button>
                    <button
                      type="button"
                      disabled={busy || approvingAll || decidingId === a.id}
                      onClick={() => decideAction(a.id, 'reject')}
                      className={`${ghostChip}`}
                      data-testid={`cofounder-reject-${a.id}`}
                    >
                      {decidingId === a.id && decidingAction === 'reject' && <Loader2 className="h-3 w-3 animate-spin" />}
                      No
                    </button>
                    <button
                      type="button"
                      disabled={busy || approvingAll || decidingId === a.id}
                      onClick={() => decideAction(a.id, 'approve')}
                      className="rounded-full px-3 py-1.5 text-xs font-extrabold bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d]"
                      data-testid={`cofounder-approve-${a.id}`}
                    >
                      {decidingId === a.id && decidingAction === 'approve' && <Loader2 className="h-3 w-3 animate-spin" />}
                      Yes, do it
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-1 p-1 rounded-full border border-white/12 bg-[#1c1914] w-fit">
          {[
            { id: 'activity', label: 'Activity', icon: Activity },
            { id: 'tasks', label: 'Tasks', icon: Rocket },
            { id: 'chat', label: 'Chat', icon: MessageCircle },
          ].map((t) => {
            const Icon = t.icon;
            const on = panel === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setPanel(t.id)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition ${
                  on
                    ? 'bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d] shadow-[0_2px_0_0_#16130d]'
                    : 'text-[#c4b8a5] hover:text-white border-2 border-transparent'
                }`}
                data-testid={`cofounder-tab-${t.id}`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {panel === 'activity' && (
          <div className={`${card} p-4 max-h-[60vh] overflow-y-auto`} data-testid="cofounder-activity">
            {activity.length === 0 ? (
              <div className="text-center text-[#c4b8a5] text-sm font-medium py-10">
                {total === 0 ? 'Press Launch to start.' : 'No activity yet.'}
              </div>
            ) : activity.map((ev) => <ActivityRow key={ev.id} ev={ev} navigate={navigate} />)}
          </div>
        )}

        {panel === 'tasks' && (
          <div className="space-y-2" data-testid="cofounder-roadmap">
            {tasks.length === 0 ? (
              <div className={`${card} p-8 text-center text-[#c4b8a5] text-sm font-medium`}>
                Press Launch to generate tasks.
              </div>
            ) : tasks.map((t) => (
              <TaskCard key={t.id} task={t} onOpen={setOpenTask} navigate={navigate} />
            ))}
          </div>
        )}

        {panel === 'chat' && (
          <div className={`${card} flex flex-col overflow-hidden min-h-[380px]`} data-testid="cofounder-chat">
            <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[50vh]">
              {messages.length === 0 && (
                <p className="text-sm text-[#c4b8a5] font-medium">Try: “focus on Austin coffee shops” or “approve”</p>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`text-sm rounded-2xl px-3.5 py-2.5 max-w-[90%] font-medium ${
                  m.role === 'user'
                    ? 'ml-auto bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d]'
                    : 'mr-auto bg-[#0e0c0a] text-[#f5f0e6] border border-white/10'
                }`}>
                  {m.content}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <form onSubmit={sendChat} className="border-t border-[#fbbf0f]/20 p-3 flex gap-2 bg-[#141210]">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder={sessionReadOnly ? 'Read-only' : 'Message your co-founder…'}
                disabled={sessionReadOnly}
                className={`${inputCls} flex-1 disabled:opacity-50`}
                data-testid="cofounder-chat-input"
              />
              <button type="submit" disabled={sessionReadOnly || chatBusy || !chatInput.trim()}
                className={`${btnPrimary} px-3`} data-testid="cofounder-chat-send">
                {chatBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </div>
        )}
      </div>
      <DeliverableModal task={openTask} onClose={() => setOpenTask(null)} navigate={navigate} />
    </DashboardShell>
  );
};

export default AICofounderDashboard;

